/**
 * ErrorBoundary.jsx — Proteção global contra crashes de componentes.
 *
 * Captura qualquer erro não tratado na árvore de componentes filhos e
 * exibe uma UI de fallback amigável em vez de deixar a tela em branco.
 *
 * Conforme Rule 7 (Error Handling): nunca silencia erros.
 * Todos os erros são logados com contexto (component + stack).
 *
 * @module components/shared/ErrorBoundary
 */
import React from 'react';

/**
 * Componente de Error Boundary global.
 * Deve envolver a raiz da aplicação para capturar crashes de qualquer rota.
 *
 * @example
 * <ErrorBoundary>
 *   <App />
 * </ErrorBoundary>
 */
export default class ErrorBoundary extends React.Component {
  /** @param {{ children: React.ReactNode, fallback?: React.ReactNode }} props */
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
    this.handleReset = this.handleReset.bind(this);
  }

  /**
   * Atualiza estado quando um erro é capturado.
   * React chama isso durante o render do filho que errou.
   * @param {Error} error
   */
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  /**
   * Loga o erro com contexto para debugging.
   * Chamado após o render com o erro capturado.
   * @param {Error} error
   * @param {React.ErrorInfo} info
   */
  componentDidCatch(error, info) {
    // Log estruturado conforme Rule 20 (Logging & Observability)
    console.error('[ErrorBoundary] Componente crashed:', {
      error: error?.message,
      stack: error?.stack?.split('\n').slice(0, 5).join('\n'),
      componentStack: info?.componentStack?.split('\n').slice(0, 5).join('\n'),
    });
    this.setState({ info });
  }

  /** Reseta o estado de erro para tentar re-renderizar. */
  handleReset() {
    this.setState({ hasError: false, error: null, info: null });
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    // Fallback customizado (se fornecido pelo pai)
    if (this.props.fallback) {
      return this.props.fallback;
    }

    // UI de fallback padrão — minimalista e consistente com o design do sistema
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: '#0f172a',
        gap: '16px',
        padding: '32px',
        textAlign: 'center',
      }}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#f59e0b"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ width: 48, height: 48 }}
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>

        <h2 style={{ color: '#f1f5f9', margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>
          Algo deu errado nesta tela
        </h2>

        <p style={{ color: '#94a3b8', margin: 0, fontSize: '0.875rem', maxWidth: 400 }}>
          Um componente encontrou um erro inesperado. Seus dados estão seguros — apenas esta tela precisa ser recarregada.
        </p>

        {/* Detalhes técnicos (apenas em dev) */}
        {process.env.NODE_ENV === 'development' && this.state.error && (
          <pre style={{
            background: '#1e293b',
            border: '1px solid #334155',
            borderRadius: 8,
            padding: '12px 16px',
            fontSize: '0.75rem',
            color: '#f87171',
            maxWidth: 600,
            overflowX: 'auto',
            textAlign: 'left',
            maxHeight: 120,
            overflow: 'auto',
          }}>
            {this.state.error.message}
          </pre>
        )}

        <div style={{ display: 'flex', gap: 12 }}>
          <button
            onClick={this.handleReset}
            style={{
              background: 'var(--color-primary-fill)',
              border: 'none',
              borderRadius: 8,
              color: '#fff',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: 500,
              padding: '10px 20px',
              transition: 'background 0.15s',
            }}
          >
            Tentar novamente
          </button>
          <button
            onClick={() => window.location.href = '/'}
            style={{
              background: '#1e293b',
              border: '1px solid #334155',
              borderRadius: 8,
              color: '#94a3b8',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: 500,
              padding: '10px 20px',
            }}
          >
            Voltar ao início
          </button>
        </div>
      </div>
    );
  }
}
